import { create } from 'zustand';

import type {
  EffortLevel,
  ExecutableToolName,
  JobId,
  Member,
  MemberBinding,
} from '@ai-studio/shared';

import { ApiError, api } from '../lib/api.js';

export interface JobCatalogEntry {
  id: JobId;
  label: string;
  summary: string;
  tools: readonly string[];
  selfInitiating: boolean;
  boardWrite: boolean;
  canAssign: boolean;
}

interface TeamState {
  members: Member[];
  catalog: JobCatalogEntry[];
  jobTools: Partial<Record<JobId, ExecutableToolName[]>>;
  toolsProjectId: number | null;
  loadJobTools: (projectId: number) => Promise<void>;
  saveJobTools: (projectId: number, job: JobId, tools: ExecutableToolName[]) => Promise<boolean>;
  error: string | null;
  loading: boolean;

  load: (projectId: number) => Promise<void>;
  /** Drop the roster, e.g. when the project changes or closes. */
  reset: () => void;
  loadCatalog: () => Promise<void>;
  create: (
    projectId: number,
    input: {
      name: string;
      jobs: JobId[];
      bindings?: MemberBinding[];
      enabled?: boolean;
      autonomy?: Member['autonomy'];
      autoAllowShell?: boolean;
      autoAllowEdits?: boolean;
      autoApproveAll?: boolean;
      autonomousWork?: boolean;
      reverseEngineerGame?: boolean;
      /** Null (or omitted) leaves effort to the provider/model default. */
      effort?: EffortLevel | null;
      ultracode?: boolean;
    },
  ) => Promise<Member | null>;
  patch: (
    projectId: number,
    memberId: number,
    patch: Partial<
      Pick<
        Member,
        | 'name'
        | 'enabled'
        | 'autonomy'
        | 'autoAllowShell'
        | 'autoAllowEdits'
        | 'autoApproveAll'
        | 'autonomousWork'
        | 'reverseEngineerGame'
        | 'effort'
        | 'ultracode'
        | 'bindings'
      >
    > & {
      jobs?: JobId[];
    },
  ) => Promise<boolean>;
  remove: (projectId: number, memberId: number) => Promise<boolean>;
  upsertLocal: (member: Member) => void;
  /** True when a member can perform this job. */
  canDo: (member: Member, job: JobId) => boolean;
}

export const useTeam = create<TeamState>((set, get) => ({
  members: [],
  catalog: [],
  jobTools: {},
  toolsProjectId: null,
  error: null,
  loading: false,

  load: async (projectId) => {
    set({ loading: true, error: null });
    try {
      const result = await api.get<{ members: Member[] }>(`/projects/${projectId}/members`);
      set({ members: result.members, loading: false });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof ApiError ? error.message : 'Could not load the team.',
      });
    }
  },

  // Cleared rather than left in place: a roster from another project resolves
  // member ids to the wrong names, and an empty one renders every agent as a
  // bare "member <id>". Both read as a glitch rather than as missing data.
  reset: () =>
    set({ members: [], jobTools: {}, toolsProjectId: null, error: null, loading: false }),

  loadJobTools: async (projectId) => {
    set({ toolsProjectId: projectId, jobTools: {} });
    try {
      const result = await api.get<{ settings: Record<JobId, ExecutableToolName[]> }>(
        `/projects/${projectId}/job-tools`,
      );
      if (get().toolsProjectId === projectId) set({ jobTools: result.settings });
    } catch (error) {
      if (get().toolsProjectId === projectId)
        set({ error: error instanceof ApiError ? error.message : 'Could not load job tools.' });
    }
  },
  saveJobTools: async (projectId, job, tools) => {
    try {
      const result = await api.patch<{ settings: Record<JobId, ExecutableToolName[]> }>(
        `/projects/${projectId}/job-tools/${job}`,
        { tools },
      );
      if (get().toolsProjectId === projectId) set({ jobTools: result.settings, error: null });
      return true;
    } catch (error) {
      if (get().toolsProjectId === projectId)
        set({ error: error instanceof ApiError ? error.message : 'Could not save job tools.' });
      return false;
    }
  },

  loadCatalog: async () => {
    try {
      const result = await api.get<{ jobs: JobCatalogEntry[] }>('/catalog');
      set({ catalog: result.jobs });
    } catch (error) {
      set({ error: error instanceof ApiError ? error.message : 'Could not load the catalog.' });
    }
  },

  create: async (projectId, input) => {
    try {
      const result = await api.post<{ member: Member }>(`/projects/${projectId}/members`, input);
      set((state) => ({ members: [...state.members, result.member] }));
      return result.member;
    } catch (error) {
      set({ error: error instanceof ApiError ? error.message : 'Could not add the member.' });
      return null;
    }
  },

  patch: async (projectId, memberId, patch) => {
    try {
      const result = await api.patch<{ member: Member }>(
        `/projects/${projectId}/members/${memberId}`,
        patch,
      );
      set((state) => ({
        members: state.members.map((m) => (m.id === memberId ? result.member : m)),
      }));
      return true;
    } catch (error) {
      set({ error: error instanceof ApiError ? error.message : 'Could not update the member.' });
      return false;
    }
  },

  remove: async (projectId, memberId) => {
    try {
      await api.del(`/projects/${projectId}/members/${memberId}`);
      set((state) => ({ members: state.members.filter((m) => m.id !== memberId) }));
      return true;
    } catch (error) {
      set({ error: error instanceof ApiError ? error.message : 'Could not remove the member.' });
      return false;
    }
  },

  upsertLocal: (member) => {
    set((state) => {
      const exists = state.members.some((m) => m.id === member.id);
      return {
        members: exists
          ? state.members.map((m) => (m.id === member.id ? member : m))
          : [...state.members, member],
      };
    });
  },

  canDo: (member, job) => member.jobs.includes(job),
}));
